import { TranslationType } from '../types';

export const en: TranslationType = {
  common: {
    wallets: 'Wallets',
    dashboard: 'Dashboard',
    settings: 'Settings',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    language: 'Language',
    english: 'English',
    chinese: '中文',
    russian: 'Русский',
    german: 'Deutsch',
    indonesian: 'Bahasa Indonesia',
    korean: '한국어',
    japanese: '日本語',
    thai: 'ภาษาไทย',
    blog: 'Blog'
  },
  footer: {
    quickLinks: 'Quick Links',
    about: 'About Us',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    contact: 'Contact Us',
    copyright: '© 2025 XCH.live. All rights reserved.',
    description: 'Advanced monitoring platform for Chia blockchain wallets. Get real-time alerts for transactions, secure your assets with instant notifications, and stay in control of your cold wallet activity.',
    connectWithUs: 'Connect With Us',
    builtBy: 'Built with ❤️ by Spacescan.io'
  },
  home: {
    hero: {
      title: 'Monitor Your Chia Cold Wallet',
      subtitle: 'Get instant Email notifications for XCH transactions',
      input: {
        label: 'Enter master key (96 characters) or XCH address',
        placeholder: 'Enter master key (96 characters) or XCH address',
        helper: 'Enter your master key or XCH address to start monitoring'
      },
      buttons: {
        cardprefix: '$12',
        card: 'Pay with Card',
        strprefix: '50% OFF',
        str: 'Pay with STR'
      }
    },
    features: {
      title: 'Protect Your Chia Before It\'s Too Late',
      subtitle: 'Don\'t wait for hackers—proactively protect your cold wallet with 24/7 monitoring',
      vault: {
        title: 'Enable Vault & Clawback',
        description: 'Secure your assets with advanced vault features and clawback protection'
      },
      monitoring: {
        title: 'Secure Monitoring',
        description: 'Monitor your cold wallet activity without exposing private keys'
      },
      alerts: {
        title: 'Instant Alerts',
        description: 'Receive immediate email notifications for any transaction'
      },
      recovery: {
        title: 'Clawback Recovery',
        description: 'Recover funds from unauthorized transactions within the clawback period'
      }
    },
    metrics: {
      title: 'Our Impact in Numbers',
      walletsMonitored: {
        label: 'Wallets Monitored'
      },
      alertsSent: {
        label: 'Alerts Sent'
      },
      transactionsAnalyzed: {
        label: 'Transactions Analyzed'
      }
    },
    testimonials: {
      title: 'What Our Users Say',
      items: [
        {
          quote: 'XCH.live gives me peace of mind knowing my cold wallet is being monitored 24/7. The instant notifications are a game-changer.',
          author: 'Michael Chen',
          role: 'Chia Farmer'
        },
        {
          quote: 'The setup was incredibly simple, and the monitoring is reliable. Exactly what I needed for my Chia holdings.',
          author: 'Sarah Johnson',
          role: 'Crypto Investor'
        },
        {
          quote: 'Best monitoring solution for Chia wallets. The email alerts are fast and the dashboard is very intuitive.',
          author: 'David Kim',
          role: 'Blockchain Developer'
        }
      ]
    }
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage your wallet settings',
    notifications: {
      title: 'Notifications',
      emailNotifications: 'Email Notifications',
      emailDescription: 'Receive transaction updates via email',
      threshold: {
        title: 'Transaction Threshold',
        description: 'Minimum XCH amount to trigger notifications (minimum 1 XCH)'
      }
    },
    walletInfo: {
      title: 'Wallet Information',
      masterKey: 'Master Key'
    },
    actions: {
      save: 'Save Changes',
      delete: 'Delete Wallet'
    },
    messages: {
      success: 'Settings saved successfully!',
      error: {
        generic: 'Failed to update settings',
        threshold: 'Threshold must be at least 1 XCH',
        delete: 'Failed to delete wallet'
      },
      deleteConfirm: 'Are you sure you want to delete this wallet? This action cannot be undone.'
    }
  },
  wallets: {
    title: 'Your Wallets',
    subtitle: 'Manage and monitor your cold wallets',
    status: {
      active: 'Monitoring Active',
      pending: 'Payment Pending',
      expired: 'Monitoring Expired',
      timeLeft: {
        days: '{{days}}d {{hours}}h left',
        hours: '{{hours}}h {{minutes}}m left',
        minutes: '{{minutes}}m left',
        lessThanMinute: 'Less than 1m left'
      }
    },
    actions: {
      payWithCard: 'Pay with Card',
      completeCryptoPayment: 'Complete Crypto Payment',
      renewMonitoring: 'Renew Monitoring',
      dashboard: 'Dashboard',
      settings: 'Settings',
      addFirstWallet: 'Add Your First Wallet',
      removeWallet: 'Remove Wallet'
    },
    messages: {
      noWallets: 'No wallets found'
    }
  },
  dashboard: {
    title: 'Wallet Dashboard',
    tabs: {
      events: 'Events',
      addresses: 'Addresses'
    },
    addresses: {
      totalAddresses: 'Total Addresses: {{count}}',
      index: 'Index',
      address: 'Address',
      actions: 'Actions'
    },
    events: {
      loadMore: 'Load More',
      noEvents: 'No events found',
      sent: 'Sent',
      received: 'Received',
      spent: 'Spent',
      pending: 'Pending',
      failed: 'Failed',
      showDetails: 'Show Details',
      hideDetails: 'Hide Details',
      block: 'Block',
      viewOnSpacescan: 'View on Spacescan',
      transactionHash: 'Transaction Hash',
      nftsReceived: 'NFTs Received',
      nftsSent: 'NFTs Sent',
      nftTransaction: 'NFT Transaction',
      viewNFTDetails: 'View NFT Details',
      receivedNFTs: 'Received NFTs',
      sentNFTs: 'Sent NFTs'
    }
  },
  faq: {
    title: 'Frequently Asked Questions',
    security: {
      question: 'How secure is your monitoring service?',
      answer: 'Our service operates with the highest security standards, utilizing only your master public key for monitoring purposes. Your funds remain completely secure as we never have access to private keys or spending capabilities. We implement BLS-12-381 cryptographic standards, where private keys (32 bytes) and public keys (48 bytes) remain separate and secure.',
      learnMore: 'Learn more about BLS Keys in Chia →'
    },
    monitoring: {
      question: 'What is your transaction monitoring frequency?',
      answer: 'Our system monitors transactions in real-time, checking every new block on the Chia blockchain. We track activities across all your monitored addresses, ensuring immediate notification of any transactions affecting your wallet.'
    },
    masterKey: {
      question: 'How do I locate my wallet\'s master public key?',
      answer: 'Your master public key can be accessed through either the Chia wallet GUI or CLI. We\'ve prepared a comprehensive video guide to walk you through the process. Important: Never share your private keys or seed phrase - only the master public key is needed for our monitoring service.',
      videoTitle: 'How to locate your master public key',
      learnMore: 'Read the detailed guide on locating your keys →'
    },
    addresses: {
      question: 'How many addresses are monitored with my master key?',
      answer: 'Our service monitors 100 derivation addresses associated with your master key, providing comprehensive coverage for your wallet\'s transaction activities.'
    },
    singleAddress: {
      question: 'Can I monitor a specific address instead of using a master key?',
      answer: 'Yes, our service offers the flexibility to monitor individual addresses. You can choose to track a single specific address rather than using a master key for multiple address monitoring.'
    },
    notifications: {
      question: 'How can I customize my notification settings?',
      answer: 'You have full control over your email notifications through your wallet settings. Our dashboard provides options to customize notification preferences according to your monitoring needs.'
    },
    vault: {
      question: 'What is a Chia vault and how does it work?',
      answer: 'A Chia vault is an advanced security feature that provides enhanced protection for your digital assets. It implements additional security measures such as multi-signature requirements or time-delayed transactions. Vaults separate asset custody from coins and can require multiple signatures (m of n multi-sig) for transaction approval.',
      learnMore: 'Learn more about Chia Vaults →'
    },
    clawback: {
      question: 'What is the clawback feature in Chia?',
      answer: 'Clawback is a security feature in Chia that enables transaction reversibility within a specified timeframe. When enabled, recipients must wait for the clawback period to expire before they can access the transferred funds, providing an additional layer of transaction security.'
    },
    vaultSupport: {
      question: 'Does your service support vault and clawback features?',
      answer: 'While we monitor transactions involving vaults and clawback features, we don\'t directly manage these functions. These features need to be configured during wallet creation. Our service focuses on transaction monitoring and notification delivery for your addresses.'
    },
    tokens: {
      question: 'Do you provide monitoring for Chia tokens?',
      answer: 'Currently, our monitoring service focuses exclusively on XCH transactions. Token monitoring functionality is not supported in our current service offering.'
    },
    nft: {
      question: 'Is NFT transaction monitoring available?',
      answer: 'At present, our service specializes in XCH transaction monitoring. NFT transaction monitoring is not included in our current service capabilities.'
    },
    subscription: {
      question: 'What is your subscription and refund policy?',
      answer: 'Our service is provided on a subscription basis. Due to the immediate nature of our monitoring services, we do not offer refunds once the service is activated. Please ensure you understand our service terms before subscribing.'
    }
  },
  notFound: {
    title: 'Page Not Found',
    lostMessage: "Don't let your Chia be like this page - lost in the void!",
    securityMessage: 'Head back home and discover how to secure & monitor your XCH 24/7.',
    protectButton: 'Protect Your Chia'
  }
}; 